<template>
  <div class="shared-product-page">
    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">⏳</div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">❌</div>
      <p>{{ error }}</p>
    </div>

    <!-- Content -->
    <div v-else-if="product" class="product-content">
      <!-- Sharer Info -->
      <div class="sharer-header">
        <img v-if="sharer.avatarUrl" :src="getImageUrl(sharer.avatarUrl)" class="sharer-avatar" />
        <div v-else class="sharer-avatar-placeholder">🏪</div>
        <div class="sharer-info">
          <div class="sharer-name">{{ sharer.companyName || '批发商' }}</div>
          <div class="sharer-contact" v-if="sharer.contactPerson">
            📞 {{ sharer.contactPerson }}
          </div>
        </div>
      </div>

      <!-- Product Image -->
      <div class="product-gallery">
        <img v-if="product.image" :src="getImageUrl(product.image)" class="product-main-image" />
        <div v-else class="product-image-placeholder">📦</div>
      </div>

      <!-- Product Info -->
      <div class="product-info">
        <h1 class="product-title">{{ product.name }}</h1>
        <div class="product-price">
          <span class="price-current">S$ {{ formatPrice(product.wholesalePrice || product.marketPrice) }}</span>
          <span class="price-unit">/ {{ product.unit || '斤' }}</span>
          <span v-if="product.marketPrice && product.wholesalePrice" class="price-market">
            市场价 S$ {{ formatPrice(product.marketPrice) }}
          </span>
        </div>
        <div class="product-stock" v-if="product.availableStock">
          库存: {{ product.availableStock }} {{ product.unit || '斤' }}
        </div>
      </div>

      <!-- Product Description -->
      <div class="product-section" v-if="product.description">
        <div class="section-title">{{ $t('share.SharedProduct.line52_text') }}</div>
        <div class="section-content">{{ product.description }}</div>
      </div>

      <!-- Sharer Info Section -->
      <div class="product-section">
        <div class="section-title">{{ $t('share.SharedProduct.line58_text') }}</div>
        <div class="section-content">
          <div class="info-row">
            <span class="info-label">{{ $t('share.SharedProduct.line61_text') }}</span>
            <span class="info-value">{{ sharer.companyName || '批发商' }}</span>
          </div>
          <div class="info-row" v-if="sharer.contactPerson">
            <span class="info-label">{{ $t('share.SharedProduct.line65_text') }}</span>
            <span class="info-value">{{ sharer.contactPerson }}</span>
          </div>
          <div class="info-row" v-if="sharer.phone">
            <span class="info-label">{{ $t('share.SharedProduct.line69_text') }}</span>
            <span class="info-value">{{ sharer.phone }}</span>
          </div>
        </div>
      </div>

      <!-- Expire Info -->
      <div class="expire-info" v-if="shareInfo.expireAt">
        有效期至: {{ formatDate(shareInfo.expireAt) }}
      </div>
    </div>

    <!-- Bottom Actions -->
    <div class="bottom-actions" v-if="product">
      <button class="btn-order" @click="goOrder">{{ $t('share.SharedProduct.line83_text') }}</button>
      <button class="btn-contact" @click="contactSharer">{{ $t('share.SharedProduct.line84_text') }}</button>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import { getImageUrl } from '@/utils/image';

export default {
  name: 'SharedProduct',
  data() {
    return {
      loading: true,
      error: null,
      product: null,
      sharer: {},
      shareInfo: {}
    }
  },
  created() {
    this.loadSharedProduct()
  },
  methods: {
    getImageUrl,
    async loadSharedProduct() {
      const shareCode = this.$route.params.shareCode
      if (!shareCode) {
        this.error = '分享链接无效'
        this.loading = false
        return
      }

      try {
        const res = await api.get(`/share/product/${shareCode}`)
        this.product = res.product
        this.sharer = res.sharer || {}
        this.shareInfo = res.shareInfo || {}
      } catch (err) {
        console.error('加载分享商品失败:', err)
        this.error = err.response?.data?.message || err.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    formatPrice(price) {
      if (!price) return '0.00'
      return Number(price).toFixed(2)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('zh-CN') + ' ' + d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    },
    goOrder() {
      const shareCode = this.$route.params.shareCode
      this.$router.push({
        path: '/login',
        query: { redirect: `/s/p/${shareCode}`, shareCode }
      })
    },
    contactSharer() {
      if (this.sharer.phone) {
        // 直接触发拨号，手机端会打开拨号界面
        window.location.href = `tel:${this.sharer.phone}`
      } else {
        this.$message.info('暂无联系电话')
      }
    }
  }
}
</script>

<style scoped>
.shared-product-page {
  max-width: 500px;
  margin: 0 auto;
  background: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 80px;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #666;
}

.loading-spinner, .error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.sharer-header {
  display: flex;
  align-items: center;
  padding: 16px;
  background: white;
  border-bottom: 1px solid #eee;
}

.sharer-avatar, .sharer-avatar-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.sharer-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.sharer-contact {
  font-size: 13px;
  color: #888;
  margin-top: 4px;
}

.product-gallery {
  background: white;
  padding: 16px;
}

.product-main-image {
  width: 100%;
  border-radius: 8px;
}

.product-image-placeholder {
  width: 100%;
  height: 300px;
  background: #f0f0f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 80px;
}

.product-info {
  background: white;
  padding: 16px;
  margin-top: 8px;
}

.product-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px;
}

.product-price {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.price-current {
  font-size: 24px;
  font-weight: 700;
  color: #ff4d4f;
}

.price-unit {
  font-size: 14px;
  color: #666;
}

.price-market {
  font-size: 13px;
  color: #999;
  text-decoration: line-through;
}

.product-stock {
  margin-top: 8px;
  font-size: 13px;
  color: #888;
}

.product-section {
  background: white;
  margin-top: 8px;
  padding: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.section-content {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
}

.info-row {
  display: flex;
  padding: 8px 0;
  font-size: 14px;
}

.info-label {
  color: #888;
  width: 80px;
  flex-shrink: 0;
}

.info-value {
  color: #333;
}

.expire-info {
  text-align: center;
  padding: 12px;
  font-size: 12px;
  color: #999;
}

.bottom-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
  max-width: 500px;
  margin: 0 auto;
}

.btn-order, .btn-contact {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.btn-order {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-contact {
  background: #f5f5f5;
  color: #333;
}
</style>
